/**
 * Responsibility: the individual cells of a discovery row.
 *
 * Split out of the table when the money columns grew tooltips and nowrap
 * rules. Each of these encodes a small judgement about how a number should
 * read, and those judgements are easier to find and argue with here than
 * buried above a column list.
 */
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import Chip from '@mui/material/Chip';
import { EMPTY } from '@kit/utils';
import type { DiscoveredCard } from '../../api/client';
import { CardThumb } from './CardThumb';
import { compactMoney, compactSigned, money, toNumber } from './money';

/** Thumbnail width. Row height follows from the 5:7 card ratio. */
const THUMB_WIDTH = 58;

export function CardCell({ row }: { row: DiscoveredCard }) {
  const detail = [row.set_name, row.set_year ? `${row.set_year}` : '', row.card_number]
    .filter(Boolean)
    .join(' · ');
  return (
    <Stack direction="row" spacing={1.5} alignItems="center" sx={{ minWidth: 0, py: 0.5 }}>
      <CardThumb src={row.image_url} alt={row.card_name} width={THUMB_WIDTH} />
      <Stack spacing={0.25} sx={{ minWidth: 0 }}>
        <Typography variant="body2" sx={{ fontWeight: 600 }}>
          {row.card_name}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {detail}
        </Typography>
        {row.variant && row.variant !== 'Base' ? (
          <Chip size="small" variant="outlined" label={row.variant} sx={{ alignSelf: 'start' }} />
        ) : null}
      </Stack>
    </Stack>
  );
}

/** A grade's price over its population.
 *
 * Shortened to "$1.53K" rather than "$1,553.88", because the exact figure
 * wraps onto a second line in this column and drags the row out of line with
 * its neighbours. The full number is on hover, and in the dialog, which is
 * where someone is actually deciding what to spend.
 */
export function GradeCell({ price, population }: { price: string | null; population: number }) {
  return (
    <Stack spacing={0} sx={{ minWidth: 0, textAlign: 'right' }}>
      <Typography
        variant="body2"
        sx={{ fontWeight: 600, fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}
      >
        {compactMoney(price)}
      </Typography>
      <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
        {population.toLocaleString()} graded
      </Typography>
    </Stack>
  );
}

export function CostCell({ row }: { row: DiscoveredCard }) {
  if (row.raw_cost === null) {
    return (
      <Typography variant="body2" color="text.disabled">
        {EMPTY}
      </Typography>
    );
  }
  return (
    <Stack spacing={0} sx={{ minWidth: 0 }}>
      <Typography
        variant="body2"
        sx={{
          fontWeight: 600,
          fontVariantNumeric: 'tabular-nums',
          whiteSpace: 'nowrap',
        }}
        color={row.still_affordable ? 'text.primary' : 'error.main'}
      >
        {money(row.raw_cost)}
      </Typography>
      <Typography
        variant="caption"
        color={row.verified ? 'success.main' : 'text.secondary'}
        sx={{ whiteSpace: 'nowrap' }}
      >
        {row.verified ? 'verified' : 'reference'}
      </Typography>
    </Stack>
  );
}

export function ProfitCell({ value, hint }: { value: string | null; hint: string }) {
  const amount = toNumber(value);
  return (
    <Typography
      variant="body2"
      aria-label={hint}
      sx={{ fontWeight: 700, fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}
      color={amount === null ? 'text.disabled' : amount > 0 ? 'success.main' : 'error.main'}
    >
      {amount === null ? EMPTY : compactSigned(value)}
    </Typography>
  );
}
